import { Form, Formik } from "formik";
import { atom, useAtom } from "jotai";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { formDataAtom } from "../App";
import axios from "axios";

import { useRef, useState } from "react";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import { createJourney } from "../utils/journeyHelpers";
import { mapFormDataToPayload } from "../utils/mapFormDataToPayload";
import { ONEWAY_TRIP } from "./TravelSearch";

import { handleSubmitClick } from "@ogt/ui";
import {
  ErrorList,
  Spinner,
  CheckboxForm,
  Progressbar,
  Box,
  JourneySummary,
  Button,
  Icon,
  Heading,
} from "@ogt/ui";

import { URLS } from "src/config/urls";

interface SummaryModel {
  SeatAvailabilityDisclaimer: boolean;
  SelfResponsibility: boolean;
  gdprAccept: boolean;
}
interface Issue {
  id: string;
}

export const issueAtom = atom<Issue>({
  id: "",
});

export default function Summary() {
  const [formData, setFormData] = useAtom(formDataAtom);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const progress = 100;

  const requiredCheckbox = (errorMessage: string) =>
    Yup.bool().oneOf([true], errorMessage);

  const journeys = [
    createJourney(formData.selectedJourney, "Resa dit", "../vart-vill-ni-resa"),
  ];

  if (
    formData.selectedReturnJourney &&
    formData.selectedReturnJourney.fromName
  ) {
    journeys.push(
      createJourney(
        formData.selectedReturnJourney,
        "Resa tillbaka",
        "../valj-aterresa"
      )
    );
  }

  const TravelContacts = [
    {
      Headline: "Vilka är ni som ska resa?",
      organization: formData.schoolorg || "Ej angivet",
      travelQty: formData.travelQty || "Ej angivet",
      contactPerson: formData.contactperson || "Ej angivet",
      phone: formData.phonenumberschoolorg || "Ej angivet",
      contactEmail: formData.mailadress || "Ej angivet",
      contactPhone: formData.phonenumber || "Ej angivet",
    },
  ];

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setIssueId] = useAtom(issueAtom);
  const formRef = useRef<HTMLFormElement>(null);

  function GetEditButtonForNavigation(path: string, text?: string) {
    return (
      <Button
        type="button"
        variant={"ghost"}
        showIcon
        iconPosition="left"
        iconName="square-pen"
        onClick={() => navigate(path)}
      >
        {text ?? "Gå tillbaka och ändra"}
      </Button>
    );
  }

  return (
    <div>
      <Formik<SummaryModel>
        onSubmit={(values) => {
          const payload = mapFormDataToPayload(formData);
          const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

          if (isFormSubmitted) {
            return;
          }

          setIsFormSubmitted(true);

          axios
            .post(
              `${apiBaseUrl?.replace(/\/$/, "") ?? ""
              }/api/v1/GroupTravel/SendApplication`,
              payload,
              {
                headers: {
                  "referrer-policy": "strict-origin",
                },
              }
            )
            .then((result) => {
              setIssueId({ id: result.data });
              setFormData((prevData) => ({
                ...prevData,
                SeatAvailabilityDisclaimer: values.SeatAvailabilityDisclaimer,
                SelfResponsibility: values.SelfResponsibility,
                gdprAccept: values.gdprAccept,
              }));

              navigate("/tack-sida");
            })
            .catch((error) => {
              navigate("../ett-fel-intraffade");
            });
        }}
        initialValues={{
          SeatAvailabilityDisclaimer: false,
          SelfResponsibility: false,
          gdprAccept: false,
        }}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={Yup.object().shape({
          SeatAvailabilityDisclaimer: requiredCheckbox(
            "Ni måste acceptera villkoren för att kunna skicka er anmälan"
          ),
          SelfResponsibility: requiredCheckbox(
            "Ni måste acceptera villkoren för att kunna skicka er anmälan"
          ),
          gdprAccept: requiredCheckbox(
            "Du måste godkänna hanteringen av dina personuppgifter."
          ),
        })}
        enableReinitialize
      >
        {({ errors, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="flex justify-center h-15 md:h-auto mb-1">
                <div
                  className={`w-full text-left md:hidden ${isFormSubmitted === true ? "hide" : ""
                    }`}
                ></div>
              </div>

              <div className="w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>
            <Form>
              <Box fullwidth>
                {isFormSubmitted && <Spinner label="Skickar ..." />}
                <div
                  className={`inner-summary summary-box ${isFormSubmitted === true ? "hide" : ""
                    }`}
                >
                  <Heading
                    progress={progress}
                    headingRef={h2Ref}
                    focusable
                    id="SummaryHeading"
                  >
                    Granska och skicka in anmälan
                  </Heading>

                  {Object.keys(errors).length > 0 && (
                    <ErrorList errors={errors} />
                  )}

                  <p>
                    Har allt blivit rätt? Kolla alla uppgifter en sista gång
                    innan ni skickar in anmälan om gruppresa. För att ändra
                    något klickar ni på "Gå tillbaka och ändra". Då kommer ni
                    till sidan i formuläret där ni kan ändra. Klicka sedan på
                    "Skicka anmälan" i det sista steget.
                  </p>

                  <div className="summary-section patient-summary mb-4">
                    <div className="summary-data-wrapper flex flex-col">
                      {journeys.map((journey, index) => (
                        <JourneySummary
                          key={index}
                          journey={journey}
                          isReturnTravel={journey.Headline.includes("tillbaka")}
                          onEdit={(editPath: string) =>
                            navigate(editPath, {
                              state: { fromSummary: routerLocation.pathname },
                            })
                          }
                          onRemove={() => {
                            setFormData({
                              ...formData,
                              selectedReturnJourney: {
                                fromName: "",
                                toName: "",
                                date: "",
                                travelTime: "",
                                journeyKey: "",
                                line: "",
                                switches: "",
                                unformattedDate: "",
                              },
                              traveltype: ONEWAY_TRIP,
                            });
                          }}
                        />
                      ))}

                      {!formData.selectedReturnJourney.fromName ? (
                        <div className="last:mb-0 last:pb-0">
                          <Heading
                            as="h3"
                            className="summary-section-heading h2"
                          >
                            Behöver ni en återresa?
                          </Heading>

                          <p>
                            Om det har blivit ändrade planer så kan vi hjälpa er
                            att lägga till en återresa, er utresa kommer inte
                            att påverkas.
                          </p>

                          <Button
                            type="button"
                            variant={"outline"}
                            showIcon
                            iconPosition="left"
                            iconName="plus"
                            onClick={() => navigate("valj-aterresa")}
                          >
                            Lägg till en resa
                          </Button>
                          <hr />
                        </div>
                      ) : (
                        <></>
                      )}
                      {TravelContacts.map((entry, index) => (
                        <div key={index} className="last:mb-0 last:pb-0">
                          <Heading
                            as="h3"
                            className="summary-section-heading h2"
                          >
                            {entry.Headline}
                          </Heading>
                          <div className="mt-2 flex flex-col md:flex-row justify-between md:gap-4">
                            <div className="flex flex-col gap-2 mt-2">
                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold">
                                  Skola/Organisation:
                                </span>
                                <span className="summary-data">
                                  {entry.organization}
                                </span>
                              </div>

                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold">
                                  Antal resenärer:
                                </span>
                                <span className="summary-data">
                                  {entry.travelQty}
                                </span>
                              </div>

                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold">
                                  Kontaktperson:
                                </span>
                                <span className="summary-data">
                                  {entry.contactPerson}
                                </span>
                              </div>

                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold">
                                  Telefonnummer:
                                </span>
                                <span className="summary-data">
                                  {entry.phone}
                                </span>
                              </div>

                              <div className="flex gap-2">
                                <span className="summary-data-label font-bold">
                                  Mejladress:
                                </span>
                                <span className="summary-data">
                                  {entry.contactEmail}
                                </span>
                              </div>

                              <div className="mt-4">
                                <Heading as="h4" className="mb-2">
                                  Hur når vi dig under resan?
                                </Heading>
                                <div className="flex gap-2">
                                  <span className="summary-data-label font-bold">
                                    Telefonnummer:
                                  </span>
                                  <span className="summary-data">
                                    {entry.contactPhone}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <div className="flex flex-col">
                              <div className="summary-edit md:mb-6 mt-3 md:mt-0">
                                <div className="whitespace-nowrap summary-edit md:mb-0 mt-3 md:mt-0">
                                  {GetEditButtonForNavigation(
                                    "../kontaktuppgifter"
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                          <hr />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="summary-acceptance mb-6">
                    <Heading focusable>Villkor för resan</Heading>
                    <fieldset className="border-0 p-0 m-0">
                      <legend className="sr-only">Villkor för resan</legend>

                      <div>
                        <CheckboxForm
                          width="fullWidth"
                          name="SeatAvailabilityDisclaimer"
                        >
                          Jag har läst och förstått att Östgötatrafiken inte kan
                          garantera en plats på resan även om jag anmält det i
                          förväg.
                        </CheckboxForm>
                      </div>
                      <div>
                        <CheckboxForm
                          width="fullWidth"
                          name="SelfResponsibility"
                        >
                          Jag har läst och förstått att jag själv ansvarar för
                          att hålla mig uppdaterad om eventuella ändringar i
                          avgångstid på de resor som vi tänkt göra.
                        </CheckboxForm>
                      </div>
                      <div>
                        <CheckboxForm width="fullWidth" name="gdprAccept">
                          Jag godkänner att Östgötatrafiken sparar och hanterar
                          mina personuppgifter enligt{" "}
                          <a
                            className="flex"
                            target="_blank"
                            rel="noreferrer"
                            href={URLS.privacyPolicy}
                          >
                            relevant dataskyddslagstiftning.
                            <Icon
                              className="inline-block ml-1"
                              name="external-link"
                              size={12}
                            />
                          </a>
                        </CheckboxForm>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </Box>

              <div
                className={`flex justify-centery ${isFormSubmitted === true ? "hide" : ""
                  }`}
              >
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    type="submit"
                    disabled={isFormSubmitted}
                    onClick={handleSubmitClick(formRef)}
                  >
                    {isFormSubmitted ? (
                      <>
                        <Icon
                          name="loader-circle"
                          className="inline-block size-5 animate-spin"
                          aria-hidden="true"
                        />
                        Skickar...
                      </>
                    ) : (
                      "Skicka anmälan"
                    )}
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}
